import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import marketplaceService, { extractErrorMessage, SellerStatement } from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

/** Abrechnungsstatus → Status-Pill der Vorlage (ok / warn / neu). */
const STATEMENT_PILL: Record<string, { tone: string; icon: string }> = {
    open: { tone: 'warn', icon: 'schedule' },
    paid: { tone: 'ok', icon: 'check_circle' },
    void: { tone: 'neu', icon: 'cancel' },
};

const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');

/**
 * Monatsabrechnungen (Plan 3.7). Übersicht, kein steuerlicher Beleg — die
 * Provisionsrechnung kommt mit T4; hier nur Perioden, Beträge und Status.
 */
const SellerStatementsPanel: React.FC = () => {
    const translate = useTranslate();
    const [statements, setStatements] = useState<SellerStatement[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        marketplaceService
            .getSellerStatements()
            .then(setStatements)
            .catch((err) => setError(extractErrorMessage(err, translate('marketplaceSeller.statements.loadError'))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <div className="mk-alert err" role="alert">
                <MaterialSymbol name="error" />
                <div className="body">{error}</div>
            </div>
        );
    }
    if (statements === null) return null;

    const statusPill = (s: SellerStatement) => {
        const pill = STATEMENT_PILL[s.status] ?? { tone: 'neu', icon: 'info' };
        return (
            <span className={`mk-pill ${pill.tone}`}>
                <MaterialSymbol name={pill.icon} />
                {translate(`marketplaceSeller.statements.statuses.${s.status}`)}
                {s.paidAt ? ` · ${fmt(s.paidAt)}` : ''}
            </span>
        );
    };

    return (
        <div>
            <div className="mk-sec-t">
                <div>
                    <h2>{translate('marketplaceSeller.statements.title')}</h2>
                    <p>{translate('marketplaceSeller.statements.intro')}</p>
                </div>
            </div>
            {statements.length === 0 ? (
                <div className="mk-empty" role="status">
                    <MaterialSymbol name="receipt_long" />
                    <p>{translate('marketplaceSeller.statements.empty')}</p>
                </div>
            ) : (
                <>
                    <div className="mk-tbl-wrap wide">
                        <table className="mk-tbl">
                            <thead>
                                <tr>
                                    <th>{translate('marketplaceSeller.statements.period')}</th>
                                    <th>{translate('marketplaceSeller.statements.number')}</th>
                                    <th className="r">{translate('marketplaceSeller.statements.gross')}</th>
                                    <th className="r">{translate('marketplaceSeller.statements.fee')}</th>
                                    <th className="r">{translate('marketplaceSeller.statements.net')}</th>
                                    <th>{translate('marketplaceSeller.statements.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statements.map((s) => (
                                    <tr key={s.id}>
                                        <td className="mono">{fmt(s.periodStart)} – {fmt(s.periodEnd)}</td>
                                        <td className="mono">{s.number}</td>
                                        <td className="r">{formatEuro(s.grossTotal, s.currency)}</td>
                                        <td className="r mut">{formatEuro(s.platformFeeTotal, s.currency)}</td>
                                        <td className="r"><b>{formatEuro(s.settlementNet, s.currency)}</b></td>
                                        <td>{statusPill(s)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mk-cards">
                        {statements.map((s) => (
                            <div key={s.id} className="mk-card">
                                <div className="rowf" style={{ marginTop: 0 }}>
                                    <span className="t">{s.number}</span>
                                    {statusPill(s)}
                                </div>
                                <div className="m">{fmt(s.periodStart)} – {fmt(s.periodEnd)}</div>
                                <div className="rowf">
                                    <span className="m">{translate('marketplaceSeller.statements.net')}</span>
                                    <span className="t">{formatEuro(s.settlementNet, s.currency)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default SellerStatementsPanel;
